import React, { useId, useState } from 'react';
import { CircleCheck, Headset, Info, SendHorizontal } from 'lucide-react';
import '../../screens/settings/LetUsHelpYouPage.scss';
import { useBrandUser } from '../../hooks/useBrandUser';
import { submitHelpRequest } from '../../../shared/store/public/api';

// Let Us Help You — the brand app's screens/settings/LetUsHelpYou form, used by
// Profile → Help & Support (a page) and the site header's "Let us help you"
// (a pop-up). Same fields and rules as the app — comments need at least 50
// characters.
const MESSAGE_MIN = 50;
const MESSAGE_MAX = 500;

type Field = 'name' | 'company_name' | 'email' | 'phone' | 'message';
type Errors = Partial<Record<Field, string>>;

/** The stored mobile without a +91 / 91 / 0 prefix — the API wants 10 digits. */
const tenDigits = (value: unknown) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length > 10 ? digits.slice(-10) : digits;
};

/**
 * The help request form and its "Request sent" state. `page`: the Help &
 * Support page's layout (centred column, Send pinned on phones); `sheet`:
 * inside a pop-up.
 */
export function HelpRequestForm({ variant, onDone }: {
  variant: 'page' | 'sheet';
  /** "Done" after a request was sent. */
  onDone: () => void;
}) {
  const { user, brandName, userId, accountId } = useBrandUser();
  // Field ids unique per form (the page and the header's pop-up can both be open).
  const uid = useId();
  const idOf = (key: Field) => `${uid}help-${key}`;
  const [form, setForm] = useState<Record<Field, string>>(() => ({
    name: String(user?.full_name ?? '').trim(),
    company_name: brandName,
    email: String(user?.login_email ?? '').trim(),
    phone: tenDigits(user?.login_mobile),
    message: '',
  }));
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState<{ message: string } | null>(null);

  const set = (key: Field, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // An error clears as soon as the field is touched; it's checked again on Send.
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
    if (submitError) setSubmitError('');
  };

  const validate = (): Errors => {
    const found: Errors = {};
    if (!form.name.trim()) found.name = 'Enter your name';
    if (!form.company_name.trim()) found.company_name = 'Enter your brand name';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) found.email = 'Enter a valid email';
    if (!/^\d{10}$/.test(form.phone)) found.phone = 'Enter a 10-digit mobile number';
    const len = form.message.trim().length;
    if (len < MESSAGE_MIN) found.message = `Add ${MESSAGE_MIN - len} more character${MESSAGE_MIN - len === 1 ? '' : 's'} so the team knows how to help`;
    return found;
  };

  const onSubmit = async () => {
    if (submitting) return;
    const found = validate();
    setErrors(found);
    setSubmitError('');
    if (Object.values(found).some(Boolean)) {
      const first = (['name', 'company_name', 'email', 'phone', 'message'] as Field[]).find((f) => found[f]);
      if (first) document.getElementById(idOf(first))?.focus();
      return;
    }
    setSubmitting(true);
    const res = await submitHelpRequest({
      name: form.name.trim(),
      company_name: form.company_name.trim(),
      email: form.email.trim(),
      phone: form.phone,
      message: form.message.trim(),
      user_id: userId || undefined,
      account_id: accountId || undefined,
    });
    setSubmitting(false);
    if (res.ok) setDone({ message: res.message });
    else setSubmitError(res.message);
  };

  const messageLength = form.message.trim().length;
  const messageReady = messageLength >= MESSAGE_MIN;

  const field = (label: string, key: Field, input: React.ReactNode) => (
    <div className="brd-field brd-help__field">
      <label className="brd-field__label" htmlFor={idOf(key)}>{label}</label>
      {input}
      {!!errors[key] && <p className="brd-field__error">{errors[key]}</p>}
    </div>
  );
  const inputClass = (key: Field) => `brd-input${errors[key] ? ' brd-input--invalid' : ''}`;

  const formClassName = variant === 'page' ? 'brd-container brd-container--narrow brd-help__body' : 'brd-help__body brd-help__body--sheet';

  return done ? (
    <div className={`brd-help__done${variant === 'page' ? ' brd-container brd-container--narrow' : ''}`}>
      <CircleCheck size={56} className="brd-help__done-icon" />
      <h2 className="brd-help__done-title">Request sent</h2>
      <p className="brd-help__done-body">
        {done.message || `Thanks, ${form.name.trim().split(/\s+/)[0]}! Our team will reach out within 24 hours on ${form.email.trim()} or +91 ${form.phone}.`}
      </p>
      <button type="button" className="brd-btn brd-btn--primary brd-btn--block brd-btn--lg" onClick={onDone}>Done</button>
    </div>
  ) : (
    <form className={formClassName} onSubmit={(e) => { e.preventDefault(); onSubmit(); }} noValidate>
      <div className="brd-help__intro">
        <span className="brd-help__intro-icon"><Headset size={20} /></span>
        <div>
          <p className="brd-help__intro-title">Need a hand?</p>
          <p className="brd-help__intro-body">
            Help with your first campaign, finding creators or anything else — tell us below and our team will reach out within 24 hours.
          </p>
        </div>
      </div>

      <h2 className="brd-help__section">Your details</h2>
      <div className="brd-help__card">
        {field('Name', 'name', (
          <input id={idOf('name')} className={inputClass('name')} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Your name" maxLength={100} autoComplete="name" />
        ))}
        {field('Brand Name', 'company_name', (
          <input id={idOf('company_name')} className={inputClass('company_name')} value={form.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="Your brand" maxLength={100} autoComplete="organization" />
        ))}
        {field('Email', 'email', (
          <input id={idOf('email')} type="email" className={inputClass('email')} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@brand.com" maxLength={150} autoComplete="email" />
        ))}
        {field('Mobile', 'phone', (
          <div className={`brd-money${errors.phone ? ' brd-money--invalid' : ''}`}>
            <span className="brd-money__prefix">+91</span>
            <input
              id={idOf('phone')}
              inputMode="numeric"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="10-digit mobile number"
              maxLength={10}
              autoComplete="tel-national"
            />
          </div>
        ))}
      </div>

      <h2 className="brd-help__section">How can we help?</h2>
      <div className="brd-help__card">
        <textarea
          id={idOf('message')}
          aria-label="How can we help?"
          className={`brd-textarea${errors.message ? ' brd-input--invalid' : ''}`}
          value={form.message}
          onChange={(e) => set('message', e.target.value)}
          placeholder="E.g. I want to run my first barter campaign with food creators in Mumbai and need help choosing deliverables."
          maxLength={MESSAGE_MAX}
          rows={5}
        />
        <div className="brd-help__message-foot">
          {errors.message ? (
            <p className="brd-field__error brd-help__hint">{errors.message}</p>
          ) : (
            <p className={`brd-help__hint${messageReady ? ' brd-help__hint--ok' : ''}`}>
              {messageReady ? <CircleCheck size={14} /> : <Info size={14} />}
              {messageReady ? 'Looks good' : `At least ${MESSAGE_MIN} characters (${MESSAGE_MIN - messageLength} to go)`}
            </p>
          )}
          <span className="brd-help__counter">{`${form.message.length}/${MESSAGE_MAX}`}</span>
        </div>
      </div>

      <div className="brd-help__footer">
        {!!submitError && <p className="brd-field__error brd-help__submit-error">{submitError}</p>}
        <button type="submit" className="brd-btn brd-btn--primary brd-btn--block brd-btn--lg" disabled={submitting} aria-busy={submitting}>
          {submitting ? <span className="brd-spinner brd-spinner--light" /> : <><SendHorizontal size={16} /> Send Request</>}
        </button>
      </div>
    </form>
  );
}

export default HelpRequestForm;
